import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { AngularFireAuth } from 'angularfire2/auth';
import { User } from 'src/app/interfaces/user.interface';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import * as firebase from 'firebase';
import { Subject } from 'rxjs';
import { ShippingMethod, ShippingMethodType } from 'src/app/interfaces/salePoints.interface';
import { BehaviorSubject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { environment } from 'src/environments/environment';
import { ProductsService } from 'src/app/core/services/products/products.service';
import { LocalstorageService } from '../localstorage.service';
import { setFirebaseFilters } from 'src/app/global/utils';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  /** Saved the global shipping method */
  globalShippingMethod: ShippingMethod;
  /** Global shipping method subject */
  private sendShippingMethodGlobalSubject = new Subject<ShippingMethod>();
  /** Global shipping method observable */
  sendGlobalShippingMethodObservable = this.sendShippingMethodGlobalSubject.asObservable();
  /** Url backend */
  baseURL: String = environment.backendUrl;
  headers: HttpHeaders;
  isBrowser: boolean;

  constructor(
    private afAuth: AngularFireAuth,
    private http: HttpClient,
    private productsService: ProductsService,
    private localStorage: LocalstorageService,
    @Inject(PLATFORM_ID) private platFormId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platFormId);
    this.initShippingMethodData();
  }


  getUserRef(collectionName: string, filters: any[] = [], orderBy: string = '', limit: number = 0) {
    let params = setFirebaseFilters(filters, orderBy, limit);
    return this.http.get(`${this.baseURL}collections/getCollection/${collectionName}`, { params });
  }

  getUserRefNumber(collectionName: string, filters: any[] = [], orderBy: string = '', limit: number = 0) {
    this.headers = new HttpHeaders({ 'Content-Type': 'application/json' });
  
    let queryParams = '';
  
    if (filters.length > 0) {
      const encodedFilters = encodeURIComponent(JSON.stringify(filters));
      queryParams += `filters=${encodedFilters}`;
    }
  
    if (orderBy) {
      queryParams += queryParams ? `&orderBy=${orderBy}` : `orderBy=${orderBy}`;
    }
  
    if (limit > 0) {
      queryParams += queryParams ? `&limit=${limit}` : `limit=${limit}`;
    }
  
    // Construir la URL manualmente con los filtros
    const url = `${this.baseURL}collections/getCollection/${collectionName}?${queryParams}`;
  
    return this.http.get(url, { headers: this.headers });
  }

  /**
   * Edit an user
   * @param user
   */
  async updateUserData(user: User) {
    // Mientras se resuelve la info de los punto de ventas de los distribuidores.
    for (const item in user) {
      if (user[item] === undefined) {
        delete user[item];
      }
      if (item == 'direcciones' && user[item][0] == undefined) {
        user[item] = []
      }
    }
    try {
      return this.http.put(`${this.baseURL}collections/updateCollection/usuarios/${user.uid}`, user).toPromise();
    } catch (error) {
      console.log(`Error updating user: ${error}`);
    }
  }

  /**Functions which get ip address from backend */
  getIpAddress() {
    this.headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.get(this.baseURL + '' + 'api/get-ip', {
      headers: this.headers,
    });
  }

  /**Get the user id from user logged */
  getUserIdLogged() {
    return this.afAuth.authState.pipe(map((auth) => auth?.uid));
  }

  /** Gets the logged user */
  async getLoggedUser(): Promise<User> {
    let user: User;
    try {
      let userId = (await this.afAuth.authState.toPromise()).uid;
      const filters = [{ field: 'uid', operator: '==', value: userId }];
      await this.getUserRef('usuarios', filters).toPromise().then((query: any[]) => {
        if (query.length > 0) {
          user = query[0] as User;
        }
      });
    } finally {
      return user;
    }
  }

  /**
   * Functions which return user information
   * @param idUser User id
   */
  getUserbyId(idUser: string) {
    try {
      const filters = [{ field: 'uid', operator: '==', value: idUser }];
      return this.getUserRef('usuarios', filters).pipe(map((query: any[]) => query[0]));
    } catch (error) {
    }
  }

  /**
   * Functions which return user information by phone
   * @param phone User celular
   */
  getUserbyPhone(phone: string) {
    try {
      return this.getUserRefNumber('usuarios', [{ field: 'celular', operator: '==', value: phone }]).toPromise();
    } catch (error) {
      console.log(`Error getting user by phone: ${error}`);
    }
  }
  /**
   * Functions which return user information by phone
   * @param email User email
   */
  getUserbyEmail(email: string) {
    try {
      return this.getUserRef('usuarios', [{ field: 'email', operator: '==', value: email }]).toPromise();
    } catch (error) {
      console.log(`Error getting user by email: ${error}`);
    }
  }

  /**
   * Gets the sellers from firestore
   * @returns
   */
  getSellers() {
    try {
      return this.getUserRef('vendedoresPuntosDeVenta').toPromise();
    } catch (error) {
      console.error(`Error getting sellers: ${error}`);
    }
  }

  /**
   * Change the global shipping method
   * @param shippingMethod
   */
  onSendGlobalShippingMethod(shippingMethod: ShippingMethod) {
    this.globalShippingMethod = shippingMethod;
    this.localStorage.setItem('shippingMethod', JSON.stringify(shippingMethod));
    this.sendShippingMethodGlobalSubject.next(shippingMethod);
    this.productsService.subscribeAllProducts()
  }

  /**
   * Initializes the shipping method data.
   * Retrieves the shipping method from local storage and sends it to the server.
   * If the shipping method is not found in local storage, a default shipping method is sent.
   */
  initShippingMethodData(): void {
    if (this.isBrowser) {
      const localShippingMethod = this.localStorage.getItem('shippingMethod');
      const shippingMethod = localShippingMethod ? JSON.parse(localShippingMethod) : { type: ShippingMethodType.SHIPPING };
      this.onSendGlobalShippingMethod(shippingMethod);
    }
  }
}
